import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/state/authStore";

const SSO_REDIRECT_KEY = "sso_redirect_after";

/**
 * Landing page for Microsoft SSO callbacks.
 *
 * The backend completes the OAuth exchange, sets the session cookie, and
 * redirects the browser here. This page:
 *  1. Calls checkAuth() to validate the newly-set session.
 *  2. On success, reads the intended destination from sessionStorage
 *     (written by Login before the SSO redirect) and navigates there.
 *  3. On failure (no valid session), clears stale storage and sends
 *     the user back to /login.
 *
 * sessionStorage is used rather than router state because the SSO flow
 * involves a full-page navigation away from and back to the app, which
 * wipes all React Router state.
 */
export default function SsoCallbackPage() {
  const checkAuth = useAuthStore(s => s.checkAuth);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const isLoading = useAuthStore(s => s.isLoading);
  const navigate = useNavigate();
  const hasRun = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invoke in development.
    if (hasRun.current) return;
    hasRun.current = true;

    const run = async () => {
      try {
        await checkAuth();
      } catch {
        // Network failure or unexpected server error — treat as unauthenticated.
      }

      // Read auth state directly from the store after checkAuth settles,
      // rather than relying on the reactive selector (which may not have
      // re-rendered yet).
      const authenticated = useAuthStore.getState().isAuthenticated;

      const stored = sessionStorage.getItem(SSO_REDIRECT_KEY);
      sessionStorage.removeItem(SSO_REDIRECT_KEY);

      if (authenticated) {
        navigate(stored ?? "/engage", { replace: true });
      } else {
        navigate("/login", { replace: true });
      }
    };

    run();
  }, [checkAuth, navigate]);

  // Only show a spinner while the auth check is in flight.
  // Once isLoading is false the effect above will navigate away immediately.
  if (!isLoading && !isAuthenticated) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      <p className="text-sm text-muted-foreground">Signing you in…</p>
    </div>
  );
}
